import React, { Component } from 'react'
import gql from 'graphql-tag'
import _, { get } from 'lodash'
import { Trans, t } from '@lingui/macro'

import { Modal, ButtonPrimary, ButtonClean } from 'front-end/src/components'
import MembersSelector, {
  IMembersSelectorGroup,
  IMembersSelectorMembers,
  IMembersSelectorUser
} from '../../../components/common/members-selector/MembersSelector'
import { IUser as INormUser } from '../../../../../types/User'
import { addUserToGroupMutation } from '../../../operations/userAddToGroup'
import IFilterDescriptor from '../../../../../types/FilterDescriptor'
import { apolloClientPrivate } from '../../../graphql/apolloClient'

export interface IProps {
  show: boolean
  groupId: number
  onHide: () => void
  onAdded: () => void
}

export interface IState {
  isLoading: boolean
  isSearching: boolean
  selectedUserIds: number[]
  membersFound: IMembersSelectorMembers
  membersSelected: IMembersSelectorMembers
}

type IUser = Pick<
  INormUser,
  'id' | 'userIdentity' | 'display_name' | 'image_url'
>

interface IUsersInGroup {
  users: Array<Pick<IUser, 'id'>>
}

const usersList = async (
  search: string,
  filters: IFilterDescriptor[]
): Promise<IUser[]> => {
  const query = gql`
    query userList($search: String!, $filters: [FilterDescriptor!]) {
      users(search: $search, filters: $filters) {
        id
        userIdentity
        display_name
        image_url
      }
    }
  `

  const response = await apolloClientPrivate.query({
    fetchPolicy: 'network-only',
    variables: { search, filters },
    query
  })
  return get(response, 'data.users')
}

const usersInGroup = async (id: number): Promise<IUsersInGroup> => {
  const query = gql`
    query groupById($id: PositiveInt!) {
      admin {
        group(id: $id) {
          id
          users {
            id
          }
        }
      }
    }
  `

  const response = await apolloClientPrivate.query({
    variables: { id },
    query,
    fetchPolicy: 'network-only'
  })

  return get(response, 'data.admin.group')
}

const mapUserToMembersSelector = (
  selectedUserIds: number[],
  user: IUser
): IMembersSelectorUser => {
  const { id, userIdentity, display_name: username, image_url } = user
  return {
    id,
    userIdentity,
    display_name: username,
    image_url,
    cantBeSelectedReason:
      (selectedUserIds.includes(id) && 'Already member') || undefined
  }
}

// TODO refactor to FC
class UserAddToGroupModal extends Component<IProps, IState> {
  static defaultProps = {
    show: false,
    workspaceId: undefined
  }

  state: IState = {
    isLoading: false,
    isSearching: false,
    selectedUserIds: [],
    membersFound: { users: [], groups: [] },
    membersSelected: { users: [], groups: [] }
  }

  inputRef: HTMLInputElement

  private fetchSearch = async (search: string): Promise<void> => {
    try {
      const usersResponse = await this.loadUsers(search)

      this.setState({
        membersFound: {
          users: usersResponse.map((u) =>
            mapUserToMembersSelector(this.state.selectedUserIds, u)
          ),
          groups: []
        }
      })
    } catch (e) {
      console.error(e)
    }

    this.setState({ isSearching: false })
  }

  addMember = (
    user?: IMembersSelectorUser | null,
    group?: IMembersSelectorGroup
  ) => {
    const membersSelected = { ...this.state.membersSelected }
    if (user) {
      if (!this.state.membersSelected.users.find((u) => u.id === user.id)) {
        membersSelected.users.push(user)
      }
    } else if (group) {
      if (!this.state.membersSelected.groups.find((g) => g.id === group.id)) {
        membersSelected.groups.push(group)
      }
    }

    this.setState({ membersSelected })
  }

  removeMember = (
    user?: IMembersSelectorUser | null,
    group?: IMembersSelectorGroup
  ) => {
    const membersSelected = _.cloneDeep(this.state.membersSelected)
    if (user) {
      membersSelected.users = membersSelected.users.filter(
        (u) => u.id !== user.id
      )
    } else if (group) {
      membersSelected.groups = membersSelected.groups.filter(
        (g) => g.id !== group.id
      )
    }

    this.setState({ membersSelected })
  }

  handleAddMembers = async () => {
    const { membersSelected, selectedUserIds } = this.state
    const { groupId, onAdded } = this.props

    try {
      this.setState({ isLoading: true })
      await Promise.all(
        membersSelected.users.map((u) => addUserToGroupMutation(groupId, u.id))
      )

      this.setState({
        selectedUserIds: [
          ...selectedUserIds,
          ...membersSelected.users.map((u) => u.id)
        ]
      })
      onAdded()
    } catch (err) {
      console.error(err)
    }

    this.setState({ isLoading: false })
  }

  renderControls(): JSX.Element {
    const { users, groups } = this.state.membersSelected
    const usersCount = users.length + groups.length

    return (
      <>
        <ButtonClean onClick={this.props.onHide}>
          <Trans>Cancel</Trans>
        </ButtonClean>
        <ButtonPrimary
          disabled={usersCount === 0}
          onClick={this.handleAddMembers}
          isLoading={this.state.isLoading}
        >
          <Trans>Add to group</Trans>
        </ButtonPrimary>
      </>
    )
  }

  render() {
    const { show, onHide } = this.props
    const { isSearching, membersSelected, membersFound } = this.state

    return (
      <Modal
        header={t`Add people to this group`}
        onHide={onHide}
        show={show}
        onShown={() => {
          this.inputRef.focus()
          this.loadAddressBook()
        }}
        onHidden={this.reset}
        controls={this.renderControls()}
      >
        <MembersSelector
          onQueryChange={(value) => this.searchUsers(value)}
          onAdd={(
            user?: IMembersSelectorUser | null,
            group?: IMembersSelectorGroup
          ) => {
            this.addMember(user, group)
          }}
          onRemove={(
            user?: IMembersSelectorUser | null,
            group?: IMembersSelectorGroup
          ) => {
            this.removeMember(user, group)
          }}
          isSearching={isSearching}
          membersSearched={membersFound}
          membersSelected={membersSelected}
          inputRef={(input) => {
            this.inputRef = input
          }}
        />
      </Modal>
    )
  }

  private reset = () => {
    this.setState({
      membersFound: { users: [], groups: [] },
      membersSelected: { users: [], groups: [] }
    })
  }

  private async loadAddressBook(): Promise<void> {
    const { groupId } = this.props

    const usersInGroupResponse = await usersInGroup(groupId)

    const selectedUserIds = usersInGroupResponse.users.map((u) => u.id)
    this.setState({
      selectedUserIds
    })
  }

  private loadUsers(search?: string) {
    const filters = [{ name: 'notArchived' }]

    // @ts-expect-error 9/11/2020
    return usersList(search, filters)
  }

  private searchUsers(search: string): void {
    this.setState({ isSearching: Boolean(search) })

    this.fetchSearch(search)
  }
}

export default UserAddToGroupModal
